import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  Dashboard2Base,
  Dashboard2BaseListT,
} from '@/features/Dashboard/types/types';
import useAppInit from '@/hooks/domain/App/useAppInit';
import service from '@/services/index';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { filterMap } from '@/utils/lazy';
import { getTimezoneDayjs } from '@/utils/time';

export const DASHBOARD_LIST_QUERY_KEY = 'dashboard2-list';

export const useInvalidateDashboardListQueries = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: [DASHBOARD_LIST_QUERY_KEY],
    });
};

export const useDashboardTreeListQuery = (
  folderAlias: string | null,
  searchText?: string | null,
) => {
  const wa = useWorkspaceStore((state) => state.currWorkspace)?.alias;
  const appInit = useAppInit();

  const queryFn = () => {
    if (folderAlias) {
      return service.dashboard2.fetchDashboardsInFolder(
        folderAlias,
        searchText,
      );
    }
    return service.dashboard2.fetchDashboardTreeList(searchText);
  };

  return useQuery({
    queryKey: [DASHBOARD_LIST_QUERY_KEY, 'tree', folderAlias, searchText],
    queryFn,
    enabled: appInit && !!wa,
  });
};

export const useLastUpdatedDashboardListQuery = (enabled: boolean) => {
  const wa = useWorkspaceStore((state) => state.currWorkspace)?.alias;
  const appInit = useAppInit();

  return useQuery({
    queryKey: [DASHBOARD_LIST_QUERY_KEY, 'last_updated'],
    queryFn: () =>
      service.dashboard2.fetchDashboardList({
        sort_by: 'updated_at',
        order_by: 'desc',
        limit: 5,
      }),
    enabled: appInit && !!wa && enabled,
  });
};

export const useRecentViewedDashboardListQuery = (props: {
  enabled: boolean;
  aliases: string[];
}) => {
  const wa = useWorkspaceStore((state) => state.currWorkspace)?.alias;
  const appInit = useAppInit();
  return useQuery({
    queryKey: [DASHBOARD_LIST_QUERY_KEY, 'recent'],
    queryFn: async () => {
      const res = await service.dashboard2.fetchDashboardList({
        sort_by: 'starred_at',
        order_by: 'desc',
        ids: props.aliases,
      });
      return {
        dashboards: filterMap(props.aliases, (alias) => {
          const d = res.dashboards?.find((d) => d.alias === alias);
          if (!d) return null;
          return d;
        }),
      };
    },
    enabled: appInit && !!wa && props.enabled,
  });
};

export const useStarredDashboardListQuery = (props: { enabled: boolean }) => {
  const wa = useWorkspaceStore((state) => state.currWorkspace)?.alias;
  const appInit = useAppInit();
  return useQuery({
    queryKey: [DASHBOARD_LIST_QUERY_KEY, 'starred'],
    queryFn: () =>
      service.dashboard2.fetchDashboardList({
        sort_by: 'starred_at',
        order_by: 'desc',
        starred: true,
      }),
    enabled: appInit && !!wa && props.enabled,
  });
};

export const useDashboardStarMutation = (props: {
  withInvalidate?: boolean;
  queryKeys?: (string | null)[];
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      dashboardAlias,
      starred,
    }: {
      dashboardAlias: string;
      starred: boolean;
    }) => service.dashboard2.updateDashboardStarred(dashboardAlias, starred),
    onSuccess: (data, variables) => {
      if (props.queryKeys) {
        if (props?.withInvalidate) {
          queryClient.invalidateQueries({ queryKey: [
            DASHBOARD_LIST_QUERY_KEY,
            ...props.queryKeys,
          ] });
        } else {
          queryClient.setQueryData(
            [DASHBOARD_LIST_QUERY_KEY, ...props.queryKeys],
            (old?: Dashboard2BaseListT) => {
              if (!old?.dashboards)
                return { dashboards: [] } as Dashboard2BaseListT;
              return {
                ...old,
                dashboards: old.dashboards?.map((d: Dashboard2Base) => {
                  if (d.alias === variables.dashboardAlias) {
                    return {
                      ...d,
                      starred_at: variables.starred
                        ? getTimezoneDayjs().toJSON()
                        : null,
                    };
                  }
                  return d;
                }),
              };
            },
          );
        }
      }
      if (props.onSuccess) {
        props.onSuccess();
      }
    },
  });
};
